import { useMemo } from 'react';

import { useRouter } from 'next/router';

import featureNames from '~/shared/constants/featureFlags';
import { buildMasterProductGroupCodeObjects } from '~/shared/utils/buildMasterProductGroupCodeObjects';
import useHasFeature from '~/shared/utils/useHasFeature';

import { mergeUnifiedPrimaryProductsAndRelatedData } from '../transformers/mergeUnifiedPrimaryProductsAndRelatedData';
import { NormalizedProductsV2InfiniteData } from '../transformers/normalizeProductsV2Response';
import { ProductsV2SortOption } from '../types/filters';
import { ProductsV2ProductGridResponse } from '../types/gridResponses';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useFacetTracking from './useFacetTracking';
import useProductsV2 from './useProductsV2';

type ProductsV2ProductsResponse = {
  data: NormalizedProductsV2InfiniteData<any>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  customFacetFilters: Record<string, any>;
  setCustomFacetFilters: (filters: Record<string, any>) => void;
};

const useProductsV2AndGqlProducts = (): ProductsV2ProductGridResponse => {
  const { enabled: isProductsByGroupCodeQueryEnabled } = useHasFeature(
    featureNames.PRODUCTS_BY_GROUP_CODE_QUERY
  );

  const {
    data: productsV2Results,
    isLoading: isPrimaryProductsLoading,
    isFetching: isPrimaryProductsFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    customFacetFilters,
    setCustomFacetFilters,
  } = useProductsV2() as ProductsV2ProductsResponse;

  const masterProductIds = useMemo(() => {
    return productsV2Results?.pages
      ?.flatMap((page) => page?.initialProducts?.products)
      ?.map((result) => result?.masterProductId);
  }, [productsV2Results]);

  const masterProductGroupCodeObjects = useMemo(
    () =>
      buildMasterProductGroupCodeObjects(
        productsV2Results?.pages?.flatMap(
          (page) => page?.initialProducts?.products ?? []
        ) ?? []
      ),
    [productsV2Results]
  );

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts({
    masterProductIds,
    masterProductGroupCodeObjects,
  });

  // Combine the primary products with the pricingData and relatedProductsData
  const products = useMemo(
    () =>
      mergeUnifiedPrimaryProductsAndRelatedData({
        primaryProducts: productsV2Results?.products,
        pricingData,
        relatedProductsData,
        isProductsByGroupCodeQueryEnabled,
      }),
    [
      productsV2Results,
      pricingData,
      relatedProductsData,
      isProductsByGroupCodeQueryEnabled,
    ]
  );

  const totalNumResults = productsV2Results?.pages?.[0]?.totalNumResults;

  const facets = productsV2Results?.facets || [];

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const urlParams = new URLSearchParams(query);
  const defaultSortOption =
    productsV2Results?.pages?.[0]?.initialSortOption?.value;
  const selectedSortOption = urlParams.get('sort') || defaultSortOption;

  // TODO: move into transform function
  const sortOptions = useMemo(() => {
    return productsV2Results?.pages?.[0]?.sortOptions?.map(
      (sortOption: ProductsV2SortOption) => ({
        display_name: sortOption?.defaultMessage,
        // hidden,
        sort_by: sortOption?.value,
        sort_order:
          typeof sortOption?.sort === 'string'
            ? sortOption?.sort
            : sortOption?.sort?.direction || 'desc',
        status: selectedSortOption === sortOption?.value ? 'selected' : '',
      })
    );
  }, [productsV2Results, selectedSortOption]);

  const allRegisteredFacets = productsV2Results?.facets || [];
  const allRegisteredFacetNames = allRegisteredFacets?.map(
    (facet) => facet?.name
  );
  const selectedUrlFilters =
    productsV2Results?.pages?.[0]?.productRequestParams?.[0]
      ?.aggregationFilter || {};

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  return {
    products,
    fetchNextPage,
    hasNextPage,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    isPrimaryProductsLoading,
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useProductsV2AndGqlProducts;
